// Deeper analytics (docs/plans/phase3-deeper-analytics.md §1, A1–A20): the areas, the period
// rules and the arithmetic that doesn't belong in SQL. Every figure comes from data Wodi
// already records; nothing here is a clinical threshold or target.

import type { DayPeriod } from '../reports/period.ts';
import {
  addDays,
  compareDays,
  daysBetween,
  formatDay,
  MONTH_ABBREVIATIONS,
  type CalendarDay,
} from '../time.ts';

/** The dashboards, by the key used in routes, audit and file names (plan §1.1). */
export const ANALYTICS_AREAS = ['flow', 'revenue', 'claims', 'stock', 'lab', 'inpatient'] as const;
export type AnalyticsArea = (typeof ANALYTICS_AREAS)[number];

export function isAnalyticsArea(value: string): value is AnalyticsArea {
  return (ANALYTICS_AREAS as readonly string[]).includes(value);
}

/** Plan A4: a year, as the money reports. */
export const ANALYTICS_PERIOD_LIMIT_DAYS = 366;

/** Plan A6. */
export const ANALYTICS_GRAINS = ['day', 'week', 'month'] as const;
export type AnalyticsGrain = (typeof ANALYTICS_GRAINS)[number];
export const DAY_GRAIN_MAX_DAYS = 31;
export const WEEK_GRAIN_MAX_DAYS = 92;
const DAYS_PER_BLOCK = 7;

/** Plan A17: batches expiring within this many days of today are listed. Not clinical. */
export const EXPIRY_WINDOW_DAYS = 90;

/** Plan A10: a drill-down page. */
export const DRILL_PAGE_SIZE = 50;

/** The grain for a period of `days` days, both ends included. */
export function analyticsGrain(days: number): AnalyticsGrain {
  if (!Number.isInteger(days) || days < 1) throw new RangeError('A period has at least one day');
  if (days <= DAY_GRAIN_MAX_DAYS) return 'day';
  if (days <= WEEK_GRAIN_MAX_DAYS) return 'week';
  return 'month';
}

export interface AnalyticsBucket {
  /** 0 for the first bucket; the same number SQL computes (`bucketIndexOfDay`). */
  readonly index: number;
  readonly from: CalendarDay;
  readonly to: CalendarDay;
  /** Short, for a chart axis: `22 Sep`, `Sep 2026`. Unique within a period. */
  readonly label: string;
  /** In full, for a table: `22 Sep 2026`, `22 Sep 2026 to 28 Sep 2026`. */
  readonly title: string;
}

function shortDay(day: CalendarDay): string {
  return `${String(day.day).padStart(2, '0')} ${MONTH_ABBREVIATIONS[day.month - 1] ?? ''}`;
}

function monthNumber(day: CalendarDay): number {
  return day.year * 12 + (day.month - 1);
}

function titleOf(from: CalendarDay, to: CalendarDay): string {
  return compareDays(from, to) === 0 ? formatDay(from) : `${formatDay(from)} to ${formatDay(to)}`;
}

/**
 * Which bucket an East Africa day falls in: days since the first day; 7-day blocks counted
 * from the first day; calendar months since the first day's month. The SQL in the API
 * computes the same number (apps/api/src/analytics/common.ts).
 */
export function bucketIndexOfDay(grain: AnalyticsGrain, first: CalendarDay, day: CalendarDay) {
  const days = daysBetween(first, day);
  if (grain === 'day') return days;
  if (grain === 'week') return Math.floor(days / DAYS_PER_BLOCK);
  return monthNumber(day) - monthNumber(first);
}

/** The buckets of a period, oldest first, the last one clipped to the period's end. */
export function analyticsBuckets(period: DayPeriod): {
  grain: AnalyticsGrain;
  buckets: AnalyticsBucket[];
} {
  if (compareDays(period.from, period.to) > 0) throw new RangeError('The period is backwards');
  const grain = analyticsGrain(daysBetween(period.from, period.to) + 1);
  const buckets: AnalyticsBucket[] = [];
  let from = period.from;
  while (compareDays(from, period.to) <= 0) {
    let to: CalendarDay;
    if (grain === 'day') to = from;
    else if (grain === 'week') to = addDays(from, DAYS_PER_BLOCK - 1);
    else to = { year: from.year, month: from.month, day: lastDayOfMonth(from) };
    if (compareDays(to, period.to) > 0) to = period.to;
    const label =
      grain === 'month'
        ? `${MONTH_ABBREVIATIONS[from.month - 1] ?? ''} ${String(from.year)}`
        : shortDay(from);
    buckets.push({ index: buckets.length, from, to, label, title: titleOf(from, to) });
    from = addDays(to, 1);
  }
  return { grain, buckets };
}

function lastDayOfMonth(day: CalendarDay): number {
  const next =
    day.month === 12 ? { year: day.year + 1, month: 1 } : { ...day, month: day.month + 1 };
  return addDays({ year: next.year, month: next.month, day: 1 }, -1).day;
}

/**
 * Days the stock on hand lasts at the period's average daily use (plan A17), whole days,
 * rounded down. Null when nothing was used: no rate to divide by.
 */
export function daysOfStock(onHand: number, used: number, periodDays: number): number | null {
  if (!Number.isInteger(periodDays) || periodDays < 1)
    throw new RangeError('A period has at least one day');
  if (used <= 0) return null;
  if (onHand <= 0) return 0;
  return Math.floor((onHand * periodDays) / used);
}
